import { apiClient } from './client';

/**
 * API quetes (Lot 31 / §33.13 + Lot 41B catalogue unifie).
 * Le controller backend est /api/v1/PlayerQuest (CRUD KStone + actions metier).
 */

const BASE = '/api/v1/PlayerQuest';

/**
 * Entree du catalogue unifie (Lot 41B). Combine QuestCatalog hard-code
 * et table quest_templates en DB. Champ `source` = "static" | "db".
 *
 * Les champs `eventKey` / `targetCount` sont conserves pour la retro-compat
 * (anciens templates mono-event). Ils sont mappes au runtime depuis
 * firstStepEventKey / firstStepTargetCount par {@link withLegacy}.
 */
export type QuestCategory = 'Tutoriel' | 'Principale' | 'Annexe';

export interface QuestTemplateModel {
  code: string;
  label: string;
  description: string;
  questTypeId: number;
  questTypeCode: string;
  durationDays: number | null;
  difficulty: number;
  rewardsJson: string;
  prerequisitesJson: string;
  pointsAward: number;
  stepCount: number;
  firstStepEventKey: string;
  firstStepTargetCount: number;
  source: 'static' | 'db';
  /** P2.4 -- faction sponsoring (Player = neutre). */
  sponsoringFaction: 'Player' | 'Empire' | 'Bandits';
  /** Catégorie (Tutoriel / Principale / Annexe). */
  category?: QuestCategory;
  /** @deprecated alias de firstStepEventKey, retire au Lot 41D. */
  eventKey: string;
  /** @deprecated alias de firstStepTargetCount, retire au Lot 41D. */
  targetCount: number;
}

type ServerTemplate = Omit<QuestTemplateModel, 'eventKey' | 'targetCount'>;

function withLegacy(raw: ServerTemplate): QuestTemplateModel {
  return {
    ...raw,
    eventKey: raw.firstStepEventKey,
    targetCount: raw.firstStepTargetCount,
  };
}

export interface PlayerQuestModel {
  id: number;
  playerUniverseId: number;
  questTypeId: number;
  eventKey: string;
  targetCount: number;
  progress: number;
  questStatus: number;
  startedAt: string;
  expiresAt: string | null;
  completedAt: string | null;
  /** Catégorie (Tutoriel / Principale / Annexe), dénormalisée. */
  category?: QuestCategory;
  rewardsJson: string;
}

export interface AcceptQuestPDTO {
  playerUniverseId: number;
  questCode: string;
}

export interface AcceptCustomQuestPDTO {
  playerUniverseId: number;
  questTypeId: number;
  eventKey: string;
  targetCount: number;
  rewardsJson: string;
  durationDays: number | null;
}

export interface ReportQuestProgressPDTO {
  playerUniverseId: number;
  eventKey: string;
  increment: number;
}

export async function fetchCatalog(): Promise<QuestTemplateModel[]> {
  const { data } = await apiClient.get<ServerTemplate[]>(`${BASE}/catalog`);
  return data.map(withLegacy);
}

/**
 * Lot 41B : l'endpoint /available/... renvoie maintenant des QuestTemplate
 * (catalogue unifie) plutot que des paires {code, template}. On garde la
 * shape AvailableQuestItem pour ne pas casser les appelants — code et
 * template pointent vers le meme objet.
 */
export interface AvailableQuestItem {
  code: string;
  template: QuestTemplateModel;
}

export async function fetchAvailableForPlayer(playerUniverseId: number): Promise<AvailableQuestItem[]> {
  const { data } = await apiClient.get<ServerTemplate[]>(`${BASE}/available/${playerUniverseId}`);
  return data.map(raw => {
    const t = withLegacy(raw);
    return { code: t.code, template: t };
  });
}

export async function fetchPlayerQuests(playerUniverseId: number, questStatus?: number): Promise<PlayerQuestModel[]> {
  const { data } = await apiClient.get<PlayerQuestModel[]>(`${BASE}/by-player/${playerUniverseId}`, {
    params: questStatus !== undefined ? { status: questStatus } : undefined,
  });
  return data;
}

/**
 * Nombre de quetes "claimable" (a recuperer) : quetes Active (status=0)
 * dont la progression a atteint la target. Cote UI c'est le bouton
 * "Recuperer" dans QuestsView. Pas d'endpoint dedie -- on filtre cote
 * client. V1 acceptable tant que le nombre de quetes par joueur reste
 * borne (< 100). Si ca explose un jour, faire un endpoint /claimable-count.
 */
export async function getClaimableCount(playerUniverseId: number): Promise<number> {
  const quests = await fetchPlayerQuests(playerUniverseId, 0); // 0 = Active
  return quests.filter(q => q.targetCount > 0 && q.progress >= q.targetCount).length;
}

export async function acceptQuest(body: AcceptQuestPDTO): Promise<PlayerQuestModel> {
  const { data } = await apiClient.post<PlayerQuestModel>(`${BASE}/accept`, body);
  return data;
}

export async function acceptCustomQuest(body: AcceptCustomQuestPDTO): Promise<PlayerQuestModel> {
  const { data } = await apiClient.post<PlayerQuestModel>(`${BASE}/accept-custom`, body);
  return data;
}

export async function completeQuest(questId: number): Promise<PlayerQuestModel> {
  const { data } = await apiClient.post<PlayerQuestModel>(`${BASE}/${questId}/complete`);
  return data;
}

export async function cancelQuest(questId: number): Promise<PlayerQuestModel> {
  const { data } = await apiClient.post<PlayerQuestModel>(`${BASE}/${questId}/cancel`);
  return data;
}

export async function reportProgress(body: ReportQuestProgressPDTO): Promise<{ affectedQuests: number }> {
  const { data } = await apiClient.post<{ affectedQuests: number }>(`${BASE}/report-progress`, body);
  return data;
}
